import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ScanLine, FileText, Timer, BadgeCheck, GraduationCap, ShieldAlert, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";
import { fmtDT, fmtD, dayjs } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { StatCard } from "@/components/Common";
import { HERO_IMG } from "@/pages/Login";
import { PriorityBadge, StatusBadge, kpiTone } from "@/components/Badges";

const KpiGrid = ({ k }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
    <StatCard testid="kpi-coverage" label="Copertura turni" value={k.coverage} suffix="%" target="100%" tone={kpiTone(k.coverage, 100)} icon={CalendarCheck} hint={`${k.shifts_uncovered} scoperti · ${k.substitutions} sostituz.`} />
    <StatCard testid="kpi-rounds" label="Ronde completate" value={k.rounds} suffix="%" target="≥ 98%" tone={kpiTone(k.rounds, 98)} icon={ScanLine} hint={`${k.rounds_done}/${k.rounds_planned} checkpoint`} />
    <StatCard testid="kpi-reports" label="Report di turno" value={k.reports} suffix="%" target="100%" tone={kpiTone(k.reports, 100)} icon={FileText} hint="Passaggi di consegna" />
    <StatCard testid="kpi-sla" label="Presa in carico entro SLA" value={k.sla_compliance} suffix="%" target="≥ 95%" tone={kpiTone(k.sla_compliance, 95)} icon={Timer} hint={`${k.tickets_open} aperti · ${k.sla_breached_open} fuori SLA`} />
    <StatCard testid="kpi-p1" label="Tempo medio P1" value={k.p1_avg_min} suffix=" min" target="≤ 5 min" tone={k.p1_avg_min <= 5 ? "ok" : "bad"} icon={Clock} hint="Ticket critici" />
    <StatCard testid="kpi-nc" label="NC chiuse nei tempi" value={k.nc_on_time} suffix="%" target="≥ 95%" tone={kpiTone(k.nc_on_time, 95)} icon={BadgeCheck} hint={`${k.nc_open} NC aperte`} />
    <StatCard testid="kpi-training" label="Formazione in validità" value={k.training_valid} suffix="%" target="100%" tone={kpiTone(k.training_valid, 100)} icon={GraduationCap} hint="Matrice formativa" />
    <StatCard testid="kpi-incidents" label="Incidenti / Near miss" value={`${k.incidents} / ${k.near_miss}`} icon={ShieldAlert} hint="Ultimi 7 giorni" />
  </div>
);

const HotelCard = ({ h, k, onPick }) => (
  <button onClick={onPick} className="panel overflow-hidden text-left group" data-testid={`hotel-card-${h.id}`}>
    <div className="h-28 relative overflow-hidden">
      <img src={h.image} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
      <div className="absolute bottom-2 left-4 font-mono text-[11px] text-amber-600">{h.code}</div>
    </div>
    <div className="p-4">
      <div className="font-heading font-semibold text-slate-900">{h.name}</div>
      <div className="text-xs text-slate-500 mb-3">{h.location}</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Turni", k.coverage], ["Ronde", k.rounds], ["SLA", k.sla_compliance]].map(([l, v]) => (
          <div key={l} className="bg-slate-50 rounded-md py-2">
            <div className={`font-heading font-bold ${v >= 95 ? "text-emerald-600" : "text-amber-600"}`}>{v}%</div>
            <div className="eyebrow text-[9px]">{l}</div>
          </div>
        ))}
      </div>
    </div>
  </button>
);

export default function Dashboard() {
  const { hotelId, setHotelId, hotelName, staffName } = useHotel();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard", { params: { hotel_id: hotelId } }).then((r) => setD(r.data)); }, [hotelId]);
  if (!d) return <div className="text-slate-500 font-mono text-sm">Caricamento dashboard…</div>;

  return (
    <>
      <div className="relative h-56 sm:h-64 rounded-2xl overflow-hidden fade-up" data-testid="dashboard-hero">
        <img src={HERO_IMG} alt="Sheraton Hotel" className="absolute inset-0 w-full h-full object-cover object-[center_30%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 max-w-xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-300 mb-2">Ultimi 7 giorni · {dayjs().format("dddd D MMMM YYYY")}</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="page-title">Cruscotto operativo</h1>
          <p className="text-sm text-slate-200 mt-2">{hotelId === "all" ? "Vista aggregata delle tre strutture della gara unica." : hotelName(hotelId)}</p>
        </div>
      </div>
      <KpiGrid k={d.kpi} />
      {d.per_hotel.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {d.per_hotel.map(({ hotel, kpi }) => <HotelCard key={hotel.id} h={hotel} k={kpi} onPick={() => setHotelId(hotel.id)} />)}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="panel p-5 lg:col-span-2" data-testid="trend-chart">
          <div className="eyebrow mb-4">Andamento 14 giorni</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={d.trend.map((t) => ({ ...t, day: dayjs(t.day).format("DD/MM") }))}>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ticket" name="Ticket" fill="#D97706" radius={[3, 3, 0, 0]} />
                <Bar dataKey="incidenti" name="Incidenti" fill="#EF4444" radius={[3, 3, 0, 0]} />
                <Bar dataKey="anomalie" name="Anomalie ronde" fill="#38BDF8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="panel p-5" data-testid="open-tickets-widget">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow">Ticket aperti</div>
            <Link to="/ticket" className="text-xs text-amber-600 hover:underline" data-testid="open-tickets-link">Vedi tutti</Link>
          </div>
          <div className="space-y-3">
            {!d.open_tickets.length && <div className="text-sm text-slate-500">Nessun ticket aperto</div>}
            {d.open_tickets.map((t) => (
              <div key={t.id} className="border-b border-slate-200 pb-3 last:border-0">
                <div className="flex items-center gap-2 mb-1"><PriorityBadge p={t.priority} /><StatusBadge value={t.status} /></div>
                <div className="text-sm text-slate-800">{t.title}</div>
                <div className="text-xs text-slate-500">{hotelName(t.hotel_id)} · {fmtDT(t.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5" data-testid="recent-incidents-widget">
          <div className="eyebrow mb-4">Ultimi incidenti e near miss</div>
          {d.recent_incidents.map((i) => (
            <div key={i.id} className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-200 last:border-0">
              <div>
                <div className="text-sm text-slate-800">{i.category} · {i.location}</div>
                <div className="text-xs text-slate-500">{hotelName(i.hotel_id)} · {fmtDT(i.created_at)}</div>
              </div>
              <StatusBadge value={i.type} />
            </div>
          ))}
        </div>
        <div className="panel p-5" data-testid="expiring-trainings-widget">
          <div className="eyebrow mb-4">Formazione in scadenza (30 gg)</div>
          {!d.expiring_trainings.length && <div className="text-sm text-slate-500">Nessuna scadenza imminente</div>}
          {d.expiring_trainings.slice(0, 8).map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-200 last:border-0">
              <div>
                <div className="text-sm text-slate-800">{staffName(t.staff_id)}</div>
                <div className="text-xs text-slate-500">{t.course}</div>
              </div>
              <span className={`badge-x ${t.expires_on < dayjs().format("YYYY-MM-DD") ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{fmtD(t.expires_on)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
